import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../contexts/AppContext';
import { 
  DocumentTextIcon,
  PlusIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  CalendarIcon,
  ChartBarIcon,
  LightBulbIcon,
  SparklesIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import { Report, ReportTemplate } from '../types';

const ReportsPage: React.FC = () => {
  const { isFeatureEnabled, setLoading, addNotification } = useApp();
  const [reports, setReports] = useState<Report[]>([]);
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [loading, setLocalLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplate | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'completed' | 'generating' | 'failed'>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'reports' | 'custom'>('reports');

  const loadReports = useCallback(async () => {
    setLocalLoading(true);
    setLoading('reports', true);
    try {
      const reportsData = await apiService.getReports();
      
      // Ensure we're setting an array
      if (Array.isArray(reportsData)) {
        setReports(reportsData);
      } else if (reportsData && typeof reportsData === 'object' && 'reports' in reportsData && Array.isArray((reportsData as any).reports)) {
        // Handle case where API returns { reports: [...] }
        setReports((reportsData as any).reports);
      } else {
        console.warn('Unexpected reports data format:', reportsData);
        setReports([]);
      }
    } catch (error) {
      console.error('Error loading reports:', error);
      setReports([]); // Ensure we always have an array
      addNotification({
        id: 'reports-error',
        type: 'error',
        title: 'Error Loading Reports',
        message: 'Failed to load reports. Please try again.',
        timestamp: new Date().toISOString(),
        read: false,
        category: 'system'
      });
    } finally {
      setLocalLoading(false);
      setLoading('reports', false);
    }
  }, [setLoading, addNotification]);

  const loadTemplates = useCallback(async () => {
    try {
      const templatesData = await apiService.getReportTemplates();
      
      // Ensure we're setting an array
      if (Array.isArray(templatesData)) {
        setTemplates(templatesData);
      } else if (templatesData && typeof templatesData === 'object' && 'templates' in templatesData && Array.isArray((templatesData as any).templates)) {
        // Handle case where API returns { templates: [...] }
        setTemplates((templatesData as any).templates);
      } else {
        console.warn('Unexpected templates data format:', templatesData);
        setTemplates([]);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      setTemplates([]); // Ensure we always have an array
    }
  }, []);

  useEffect(() => {
    loadReports();
    loadTemplates();
  }, [loadReports, loadTemplates]);

  const handleGenerateReport = async (type: string, config: any) => {
    try {
      const newReport = await apiService.generateReport(type, config);
      setReports(prev => [newReport, ...prev]);
      setShowCreateModal(false);
      addNotification({
        id: 'report-generated',
        type: 'success',
        title: 'Report Generated',
        message: 'Your report is being generated. You will be notified when it\'s ready.',
        timestamp: new Date().toISOString(),
        read: false,
        category: 'report'
      });
    } catch (error) {
      console.error('Error generating report:', error);
      addNotification({
        id: 'report-generate-error',
        type: 'error',
        title: 'Error Generating Report',
        message: 'Failed to generate report. Please try again.',
        timestamp: new Date().toISOString(),
        read: false,
        category: 'system'
      });
    }
  };

  const handleDownloadReport = async (id: string) => {
    try {
      const blob = await apiService.downloadReport(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading report:', error);
      addNotification({
        id: 'report-download-error',
        type: 'error',
        title: 'Error Downloading Report',
        message: 'Failed to download report. Please try again.',
        timestamp: new Date().toISOString(),
        read: false,
        category: 'system'
      });
    }
  };

  const handleScheduleReport = async (config: any) => {
    try {
      const scheduledReport = await apiService.scheduleReport(config);
      setReports(prev => [scheduledReport, ...prev]);
      setShowCreateModal(false);
      addNotification({
        id: 'report-scheduled',
        type: 'success',
        title: 'Report Scheduled',
        message: 'Your report has been scheduled successfully.',
        timestamp: new Date().toISOString(),
        read: false,
        category: 'report'
      });
    } catch (error) {
      console.error('Error scheduling report:', error);
      addNotification({
        id: 'report-schedule-error',
        type: 'error',
        title: 'Error Scheduling Report',
        message: 'Failed to schedule report. Please try again.',
        timestamp: new Date().toISOString(),
        read: false,
        category: 'system'
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'generating':
        return 'bg-blue-100 text-blue-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'scheduled':
        return 'bg-yellow-100 text-yellow-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'weekly':
        return 'bg-purple-100 text-purple-800';
      case 'daily':
        return 'bg-blue-100 text-blue-800';
      case 'monthly':
        return 'bg-green-100 text-green-800';
      case 'quarterly':
        return 'bg-orange-100 text-orange-800';
      case 'custom':
        return 'bg-indigo-100 text-indigo-800';
      case 'white_label':
        return 'bg-pink-100 text-pink-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredReports = reports.filter(report => {
    if (filterStatus !== 'all' && report.status !== filterStatus) return false;
    if (filterType !== 'all' && report.report_type !== filterType) return false;
    return true;
  });

  const formatFileSize = (fileSize: string | number) => {
    if (typeof fileSize === 'string') {
      return fileSize; // Already formatted
    }
    if (fileSize === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(fileSize) / Math.log(k));
    return parseFloat((fileSize / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="text-gray-600">Generate and manage trend analysis reports</p>
        </div>
        <div className="flex items-center space-x-4">
          {isFeatureEnabled('canCreateCustomReports') && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Generate Report
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('reports')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'reports'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Reports
          </button>
          <button
            onClick={() => setActiveTab('custom')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'custom'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Custom Reports
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'reports' ? (
        <>
          {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <DocumentTextIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-900">Total Reports</p>
              <p className="text-2xl font-bold text-gray-900">{reports.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-900">Completed</p>
              <p className="text-2xl font-bold text-gray-900">
                {reports.filter(r => r.status === 'completed').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <ClockIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-900">Scheduled</p>
              <p className="text-2xl font-bold text-gray-900">
                {reports.filter(r => r.status === 'scheduled').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <DocumentDuplicateIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-900">Templates</p>
              <p className="text-2xl font-bold text-gray-900">{templates.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center space-x-4">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 appearance-none bg-white pr-8"
          >
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="generating">Generating</option>
            <option value="failed">Failed</option>
            <option value="scheduled">Scheduled</option>
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 appearance-none bg-white pr-8"
          >
            <option value="all">All Types</option>
            <option value="weekly">Weekly</option>
            <option value="daily">Daily</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="custom">Custom</option>
            {isFeatureEnabled('canAccessWhiteLabelFeatures') && (
              <option value="white_label">White Label</option>
            )}
          </select>
        </div>
      </div>

      {/* Reports List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading reports...</p>
          </div>
        ) : filteredReports.length === 0 ? (
          <div className="text-center py-12">
            <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No reports found</h3>
            <p className="text-gray-600">Generate your first report to get started</p>
          </div>
        ) : (
          filteredReports.map((report) => (
            <div key={report.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="font-semibold text-gray-900">{report.name}</h3>
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(report.report_type)}`}>
                      {report.report_type.replace('_', ' ')}
                    </span>
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(report.status)}`}>
                      {report.status}
                    </span>
                  </div>
                  {report.description && (
                    <p className="text-gray-600 text-sm mb-3">{report.description}</p>
                  )}
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <div className="flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      Created {new Date(report.created_at).toLocaleDateString()}
                    </div>
                    {report.last_generated && (
                      <div className="flex items-center">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        Last generated {new Date(report.last_generated).toLocaleDateString()}
                      </div>
                    )}
                    {report.file_size && (
                      <div className="flex items-center">
                        <DocumentTextIcon className="h-4 w-4 mr-1" />
                        {formatFileSize(report.file_size)}
                      </div>
                    )}
                    {report.analytics && (
                      <div className="flex items-center">
                        <ChartBarIcon className="h-4 w-4 mr-1" />
                        {report.analytics.views} views
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {report.status === 'completed' && report.download_url && (
                    <button
                      onClick={() => handleDownloadReport(report.id)}
                      className="p-2 text-gray-400 hover:text-blue-600"
                      title="Download Report"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                    </button>
                  )}
                  {report.preview_url && (
                    <button
                      className="p-2 text-gray-400 hover:text-green-600"
                      title="Preview Report"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    className="p-2 text-gray-400 hover:text-yellow-600"
                    title="Edit Report"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    className="p-2 text-gray-400 hover:text-red-600"
                    title="Delete Report"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Templates Section */}
      {templates.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Report Templates</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => (
              <div key={template.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-medium text-gray-900">{template.name}</h3>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(template.type)}`}>
                    {template.type}
                  </span>
                </div>
                <p className="text-gray-600 text-sm mb-3">{template.description}</p>
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>Used {template.usage_count} times</span>
                  <div className="flex items-center">
                    <span className="text-yellow-500">★</span>
                    <span className="ml-1">{template.rating.toFixed(1)}</span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedTemplate(template);
                    setShowCreateModal(true);
                  }}
                  className="w-full mt-3 bg-purple-600 text-white px-3 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm"
                >
                  Use Template
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
        </>
      ) : (
        <CustomReportsTab />
      )}

      {/* Create Report Modal */}
      {showCreateModal && (
        <ReportModal
          template={selectedTemplate}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedTemplate(null);
          }}
          onSubmit={handleGenerateReport}
          onSchedule={handleScheduleReport}
        />
      )}
    </div>
  );
};

// Custom Reports Tab Component
const CustomReportsTab: React.FC = () => {
  const { isFeatureEnabled } = useApp();

  if (!isFeatureEnabled('canCreateCustomReports')) {
    return (
      <UpgradeCTA 
        title="Custom Reports"
        description="Create white-label reports with custom branding and advanced analytics."
        features={[
          "White-label branding & custom logos",
          "Custom report templates & layouts",
          "Advanced analytics integration",
          "Automated report generation",
          "Multiple export formats (PDF, PPT, Excel)",
          "Scheduled report delivery & sharing"
        ]}
        tier="Business"
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center py-12">
        <DocumentDuplicateIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Custom Reports</h3>
        <p className="text-gray-600">Custom reports features coming soon</p>
      </div>
    </div>
  );
};

// Report Modal Component
interface ReportModalProps {
  template?: ReportTemplate | null;
  onClose: () => void;
  onSubmit: (type: string, config: any) => Promise<void>;
  onSchedule: (config: any) => Promise<void>;
}

const ReportModal: React.FC<ReportModalProps> = ({ template, onClose, onSubmit, onSchedule }) => {
  const [formData, setFormData] = useState({
    name: template?.name || '',
    description: template?.description || '',
    type: template?.type || 'weekly',
    dateRange: {
      start_date: '',
      end_date: ''
    },
    sections: ['summary', 'trends', 'analytics'],
    charts: ['engagement_over_time', 'platform_breakdown'],
    isScheduled: false,
    scheduleConfig: {
      frequency: 'weekly',
      time: '09:00',
      timezone: 'UTC'
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.isScheduled) {
      await onSchedule(formData);
    } else {
      await onSubmit(formData.type, formData);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {template ? `Generate Report from Template` : 'Generate New Report'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XCircleIcon className="h-6 w-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Report Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Report Type
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as any }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
              >
                <option value="weekly">Weekly</option>
                <option value="daily">Daily</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={formData.dateRange.start_date}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  dateRange: { ...prev.dateRange, start_date: e.target.value }
                }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={formData.dateRange.end_date}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  dateRange: { ...prev.dateRange, end_date: e.target.value }
                }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Report Sections
            </label>
            <div className="space-y-2">
              {['summary', 'trends', 'analytics', 'insights', 'recommendations'].map((section) => (
                <label key={section} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.sections.includes(section)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData(prev => ({
                          ...prev,
                          sections: [...prev.sections, section]
                        }));
                      } else {
                        setFormData(prev => ({
                          ...prev,
                          sections: prev.sections.filter(s => s !== section)
                        }));
                      }
                    }}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 capitalize">{section}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isScheduled}
              onChange={(e) => setFormData(prev => ({ ...prev, isScheduled: e.target.checked }))}
              className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            <label className="ml-2 text-sm text-gray-700">Schedule this report</label>
          </div>

          {formData.isScheduled && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Frequency
                </label>
                <select
                  value={formData.scheduleConfig.frequency}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    scheduleConfig: { ...prev.scheduleConfig, frequency: e.target.value }
                  }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Time
                </label>
                <input
                  type="time"
                  value={formData.scheduleConfig.time}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    scheduleConfig: { ...prev.scheduleConfig, time: e.target.value }
                  }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Timezone
                </label>
                <select
                  value={formData.scheduleConfig.timezone}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    scheduleConfig: { ...prev.scheduleConfig, timezone: e.target.value }
                  }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                >
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">Eastern Time</option>
                  <option value="America/Chicago">Central Time</option>
                  <option value="America/Denver">Mountain Time</option>
                  <option value="America/Los_Angeles">Pacific Time</option>
                </select>
              </div>
            </div>
          )}

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              {formData.isScheduled ? 'Schedule Report' : 'Generate Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Upgrade CTA Component
interface UpgradeCTAProps {
  title: string;
  description: string;
  features: string[];
  tier: string;
}

const UpgradeCTA: React.FC<UpgradeCTAProps> = ({ title, description, features, tier }) => {
  const handleUpgrade = () => {
    window.location.href = '/pricing';
  };

  return (
    <div className="text-center py-12">
      <div className="max-w-2xl mx-auto">
        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <SparklesIcon className="h-8 w-8 text-purple-600" />
        </div>
        <h3 className="text-2xl font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600 mb-6">{description}</p>
        
        {/* Features List */}
        <div className="mb-8">
          <h4 className="text-lg font-medium text-gray-900 mb-4">What you'll get:</h4>
          <ul className="text-left space-y-2 max-w-md mx-auto">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start">
                <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
        
        <button
          onClick={handleUpgrade}
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
        >
          Upgrade to {tier}
        </button>
      </div>
    </div>
  );
};

export default ReportsPage; 